import type { ReactNode } from "react";
import { Icons } from "@/components/ui/icons";
import type { SlashCommand } from "@/lib/slash-commands";
import { AgentHirePopup } from "@/components/panels/agent-hire-popup";
import { InputTipBar } from "@/components/panels/input-tip-bar";
import { SlashCommandPopup } from "@/components/panels/slash-command-popup";
import { ConversationTargetChip } from "./conversation-target-chip";
import type { InputBarConversationController } from "./input-bar-types";
import { useT } from "@/i18n";
import { inputBarMessages } from "@/i18n/messages/input-bar";
import type { VoiceRememberOffer } from "./use-voice-remember-offer";
import { VoiceRememberPill } from "./voice-remember-pill";

type InputBarOverlaysProps = {
  hirePopupOpen: boolean;
  focusedAgentId?: string;
  conversationController?: InputBarConversationController;
  isOrchestrating: boolean;
  leadingNotice?: ReactNode;
  onOpenSettings?: () => void;
  onSetHirePopupOpen: (open: boolean) => void;
  projectId?: string;
  selectedIds: Set<string>;
  showTips: boolean;
  slashCommands?: SlashCommand[];
  slashHighlightedIndex: number;
  slashOpen: boolean;
  slashQuery: string;
  onSlashSelect: (command: SlashCommand) => void;
  standalone?: boolean;
  /** Offer to remember a corrected dictated word for voice. */
  remember?: VoiceRememberOffer | null;
};

export function InputBarOverlays({
  hirePopupOpen,
  focusedAgentId,
  conversationController,
  isOrchestrating,
  leadingNotice,
  onOpenSettings,
  onSetHirePopupOpen,
  projectId,
  selectedIds,
  showTips,
  slashCommands,
  slashHighlightedIndex,
  slashOpen,
  slashQuery,
  onSlashSelect,
  standalone = false,
  remember,
}: InputBarOverlaysProps) {
  const t = useT(inputBarMessages);
  return (
    <>
      {isOrchestrating && (
        <div
          className="absolute -top-6 left-1/2 flex -translate-x-1/2 items-center gap-1.5 whitespace-nowrap rounded-full bg-glass-fill px-3 py-0.5 shadow-glass-sm backdrop-blur-glass-sm"
          data-testid="orchestration-status"
        >
          <Icons.Zap className="h-2.5 w-2.5 text-accent" />
          <span className="text-appkit-mini text-muted">
            {t("orchestrating", { count: selectedIds.size - 1 })}
          </span>
        </div>
      )}
      <InputTipBar visible={showTips} />
      {remember ? (
        <VoiceRememberPill
          offer={remember}
          className="absolute bottom-full left-2 z-10 mb-1.5"
        />
      ) : null}
      {projectId && (
        <AgentHirePopup
          projectId={projectId}
          open={hirePopupOpen}
          onClose={() => onSetHirePopupOpen(false)}
          onOpenSettings={() => {
            onSetHirePopupOpen(false);
            onOpenSettings?.();
          }}
        />
      )}
      {slashOpen && slashCommands && slashCommands.length > 0 && (
        <SlashCommandPopup
          commands={slashCommands}
          query={slashQuery}
          highlightedIndex={slashHighlightedIndex}
          onSelect={onSlashSelect}
        />
      )}
      {standalone ? (
        leadingNotice ? (
          <div
            className="absolute bottom-full left-0 w-full"
            data-testid="standalone-bar-leading-notice"
          >
            {leadingNotice}
          </div>
        ) : null
      ) : (
        <>{leadingNotice}</>
      )}
      {!standalone ? (
        <ConversationTargetChip
          focusedAgentId={focusedAgentId}
          conversationController={conversationController}
        />
      ) : null}
    </>
  );
}
